import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import {
  FIELD_BORDER,
  FIELD_BORDER_ERROR,
  FIELD_ERROR,
  FIELD_HINT,
  FIELD_LABEL,
} from '../form/field-styles';
import { uniqueId } from '../form/ids';

/** Cifre del codice monouso. */
export const CODE_LENGTH = 6;

/**
 * Campo del codice monouso a 6 cifre (app di autenticazione, codici via email). Un solo campo
 * con cifre grandi in monospazio: più semplice e accessibile di sei caselle, e compatibile con il
 * riempimento automatico (`autocomplete="one-time-code"`). Tiene solo le cifre, accetta il codice
 * incollato anche con spazi o trattini ed emette `completed` alla sesta cifra.
 */
@Component({
  selector: 'bs-code-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <label [for]="id" [class]="labelClass">{{ label() }}</label>
    <input
      #control
      [id]="id"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      pattern="[0-9]*"
      [attr.maxlength]="length"
      spellcheck="false"
      [class]="inputClass()"
      [value]="value()"
      [disabled]="disabled()"
      [attr.aria-invalid]="error() ? 'true' : null"
      [attr.aria-describedby]="describedBy()"
      [attr.data-testid]="testId()"
      (input)="onInput($event)"
      (paste)="onPaste($event)"
    />
    @if (hint(); as text) {
      <p [id]="hintId" [class]="hintClass">{{ text }}</p>
    }
    @if (error(); as message) {
      <p
        [id]="errorId"
        [class]="errorClass"
        [attr.data-testid]="testId() ? testId() + '-error' : null"
      >
        {{ message }}
      </p>
    }
  `,
})
export class CodeInputComponent {
  readonly value = model('');
  readonly label = input('Codice di verifica');
  readonly hint = input<string | null>(null);
  readonly error = input<string | null>(null);
  readonly disabled = input(false);
  readonly testId = input<string | null>(null);
  /** Il codice ha tutte le cifre (anche quando è stato incollato). */
  readonly completed = output<string>();

  protected readonly length = CODE_LENGTH;
  protected readonly id = uniqueId('bs-code');
  protected readonly hintId = `${this.id}-hint`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly labelClass = FIELD_LABEL;
  protected readonly hintClass = FIELD_HINT;
  protected readonly errorClass = FIELD_ERROR;
  private readonly control = viewChild.required<ElementRef<HTMLInputElement>>('control');

  protected readonly inputClass = computed(
    () =>
      `h-14 w-full max-w-xs rounded-xl border bg-white px-4 font-mono text-2xl tracking-widest text-ink transition-colors placeholder:text-stone-400 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-500 dark:bg-stone-900 dark:text-stone-50 dark:disabled:bg-stone-800 ${
        this.error() ? FIELD_BORDER_ERROR : FIELD_BORDER
      }`,
  );
  protected readonly describedBy = computed(() => {
    const ids = [this.hint() ? this.hintId : null, this.error() ? this.errorId : null];
    return ids.filter(Boolean).join(' ') || null;
  });

  focus(): void {
    this.control().nativeElement.focus();
  }

  protected onInput(event: Event): void {
    this.accept((event.target as HTMLInputElement).value);
  }

  protected onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text');
    if (text === undefined) {
      return;
    }
    // Si gestisce qui: con `maxlength` il browser taglierebbe "123 456" prima di togliere lo spazio.
    event.preventDefault();
    this.accept(text);
  }

  private accept(raw: string): void {
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    const previous = this.value();
    // Il campo mostra solo le cifre anche quando il valore del modello non cambia.
    this.control().nativeElement.value = digits;
    this.value.set(digits);
    if (digits.length === CODE_LENGTH && digits !== previous) {
      this.completed.emit(digits);
    }
  }
}
